import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { HelpCircle, DollarSign, TrendingUp, BarChart3, Layers, Target, FileText, ChevronRight } from 'lucide-react';

const sections = [
  {
    id: 'overview',
    icon: HelpCircle,
    title: 'Overview',
    content: [
      { heading: 'What is this tool?', text: 'This is a Deal Analysis Calculator built for evaluating land investment deals. It calculates acquisition costs, projected profits, and how those profits split between Land Finder (LF) and a JV Partner across time-based tiers.' },
      { heading: 'How it works', text: 'Enter your deal numbers on the left side. The right side updates in real-time with a Financial Summary, profit split tables for two scenarios, and (if enabled) a subdivide breakdown. You can save, load, compare, and export analyses to PDF.' },
      { heading: 'Quick start', text: '1. Enter a Purchase Price and Sale Price. 2. Review your Gross Profit in the Financial Summary. 3. Adjust Funding Structure to set who pays what. 4. Review Scenario A and B profit splits across hold-time tiers.' },
    ],
  },
  {
    id: 'acquisition',
    icon: DollarSign,
    title: 'Acquisition Costs',
    content: [
      { heading: 'Purchase Price', text: 'The agreed price to buy the land. This is the largest cost component.' },
      { heading: 'Total Acreage', text: 'The total acres in the deal. When filled, Price-per-Acre metrics appear automatically (Purchase $/Acre and Sale $/Acre).' },
      { heading: 'Due Diligence (Survey, Soil, Other DD)', text: 'Costs incurred before closing to evaluate the land: surveying, soil/perc testing, environmental studies, etc. These sum into "Total Due Diligence."' },
      { heading: 'Purchase Closing Costs', text: 'Title insurance, attorney fees, recording fees, and other costs paid at closing.' },
      { heading: 'Improvements', text: 'Post-purchase costs like roads, clearing, utilities, grading, or any capital improvements before resale.' },
      { heading: 'Total Acquisition Cost', text: 'The sum of Purchase Price + DD + Closing + Improvements. This is the total capital needed to acquire and prepare the land for sale.' },
    ],
  },
  {
    id: 'sale',
    icon: TrendingUp,
    title: 'Sale & Revenue',
    content: [
      { heading: 'Estimated Sale Price', text: 'Your projected or actual sale price for the land.' },
      { heading: 'Sale Closing Costs', text: 'Seller-side closing costs at the time of sale (title, attorney, recording, etc.).' },
      { heading: 'Sale Commission %', text: 'The real estate agent/broker commission as a percentage of the Sale Price. Default is 6%.' },
      { heading: 'Gross Sales Revenue', text: 'Sale Price minus Sale Closing Costs minus Commission. This is what you actually receive.' },
      { heading: 'Gross Profit', text: 'Gross Sales Revenue minus Total Acquisition Cost. This is the profit available for splitting between LF and the Partner.' },
      { heading: 'Margin', text: 'Gross Profit divided by Sale Price. A quick indicator of deal profitability (e.g., 30% margin means 30 cents of every sale dollar is profit).' },
    ],
  },
  {
    id: 'funding',
    icon: BarChart3,
    title: 'Funding Structure',
    content: [
      { heading: 'What is this?', text: 'This section defines how much of each cost category Land Finder funds vs. the JV Partner. The partner funds whatever LF does not.' },
      { heading: 'LF % of Purchase Price', text: 'What percentage of the Purchase Price does Land Finder put up? If 0%, the partner funds the entire purchase.' },
      { heading: 'LF % of DD / Improvements / Closing', text: 'Same logic for each cost category. Each can have a different LF funding percentage.' },
      { heading: 'Total LF Investment', text: 'Sum of all LF-funded portions. This is Land Finder\'s capital at risk.' },
      { heading: 'Total Partner Investment', text: 'Total Acquisition Cost minus LF Investment. This is the partner\'s capital at risk and drives the Cash-on-Cash (CoC) return calculation.' },
      { heading: '100% LF Funding', text: 'If LF funds everything (all percentages at 100%), Partner Investment becomes $0. A warning banner will appear if partner profit splits are still configured — click "Set LF to 100%" to zero out partner splits.' },
    ],
  },
  {
    id: 'tiers',
    icon: BarChart3,
    title: 'Profit Split Tiers (Scenarios A & B)',
    content: [
      { heading: 'What are tiers?', text: 'Profit splits change based on how long the deal takes to close. Shorter hold = LF keeps more profit (reward for fast execution). Longer hold = Partner gets more (compensation for capital being tied up).' },
      { heading: 'How tiers work', text: 'Each tier has a day range (e.g., 1-180 days) and a split (e.g., 60% LF / 40% Partner). When the deal sells, the actual hold time determines which tier applies.' },
      { heading: 'Scenario A vs. B', text: 'Two independent tier structures for negotiation flexibility. You can present both to a partner and agree on one, or use them to model best-case vs. worst-case splits.' },
      { heading: 'LF Profit / Partner Profit', text: 'Gross Profit multiplied by each party\'s split percentage for that tier.' },
      { heading: 'Cash-on-Cash (CoC)', text: 'Partner Profit divided by Partner Investment. Tells the partner what return they earn on their capital.' },
      { heading: 'Annualized ROI', text: 'CoC multiplied by (365 / days). Normalizes returns to an annual basis for comparison. Only calculated for tiers with a defined end day.' },
    ],
  },
  {
    id: 'subdivide',
    icon: Layers,
    title: 'Subdivide Analysis',
    content: [
      { heading: 'When to use this', text: 'Enable Subdivide Mode when you plan to split the land into multiple parcels and sell them separately. Each parcel can have a different sale price, hold time, and commission rate.' },
      { heading: 'How cost allocation works', text: 'Acquisition costs are distributed pro-rata based on acreage. A 40-acre parcel in a 100-acre deal gets 40% of the total acquisition cost allocated to it.' },
      { heading: 'Per-parcel fields', text: 'Name: label for the parcel. Acres: size. $/Acre: auto-calculates Sale Price (or vice versa). Sale Price: expected sale amount. Closing $: sale closing costs for this parcel (leave blank to auto-allocate pro-rata from main deal). Comm%: override the default commission for this parcel. Days: expected hold time. JV: uncheck if this parcel has no partner split.' },
      { heading: 'Tier matching', text: 'Each parcel\'s "Days" field determines which Scenario A tier applies. A parcel held 120 days uses the 1-180 day tier; a parcel held 300 days uses the 271-365 day tier.' },
      { heading: 'Subdivide Summary', text: 'The terra-colored card on the right shows total acres, revenue, profit, and per-parcel breakdown.' },
      { heading: 'Scenario Comparison', text: 'When Subdivide is enabled, a Scenario Comparison table appears showing each parcel\'s profits under both Scenario A and B, with a bottom-line "LF Profit Advantage" showing which scenario is better.' },
    ],
  },
  {
    id: 'goalseek',
    icon: Target,
    title: 'Goal Seek',
    content: [
      { heading: 'What is Goal Seek?', text: 'A reverse calculator. Instead of entering a sale price and seeing the profit, you enter a target profit/margin/return and it tells you what sale price or purchase price you need.' },
      { heading: 'Target types', text: 'Profit Margin %: Target a specific margin (e.g., 25% margin). Cash-on-Cash %: Target a specific partner return (e.g., 40% CoC). Profit Amount: Target a specific dollar profit (e.g., $100,000).' },
      { heading: 'Solve for', text: 'Required Sale Price: Given your costs, what must you sell for? Maximum Purchase Price: Given the sale price, what can you afford to pay?' },
      { heading: 'Tier selector (CoC mode)', text: 'When targeting CoC, you can choose which tier\'s split percentages to solve against. Different tiers have different partner splits, so the required price changes.' },
      { heading: 'Apply to Deal', text: 'Click "Apply to Deal" to plug the calculated price directly into your deal inputs. The entire analysis recalculates instantly.' },
      { heading: 'Mini Summary', text: 'Below the result, a preview shows the resulting GP, Margin, LF Profit, Partner Profit, Partner CoC, and Partner Investment — so you can evaluate before applying.' },
    ],
  },
  {
    id: 'saving',
    icon: FileText,
    title: 'Saving, Loading & Exporting',
    content: [
      { heading: 'Save / Save New', text: 'Click "Save New" to create a new saved analysis. Once saved, "Save" updates the existing one. Analyses persist between sessions.' },
      { heading: 'Load', text: 'Click "Saved" to see all your analyses. Click any to load it. The active analysis is highlighted.' },
      { heading: 'Compare', text: 'In the Saved dialog, click the columns icon to compare any analysis side-by-side with your current one.' },
      { heading: 'Duplicate', text: 'Creates a copy of an analysis for exploring "what-if" scenarios without changing the original.' },
      { heading: 'PDF Export', text: 'Downloads a branded PDF with all sections: Acquisition, Sale, Funding, Scenarios A & B, Subdivide breakdown, Scenario Comparison, and Notes.' },
      { heading: 'Share (Public Calculator)', text: 'Generates a URL with the entire analysis encoded. Anyone with the link sees the exact same numbers — no account needed.' },
    ],
  },
];

export function HowToUseGuide({ open, onOpenChange }) {
  const [activeSection, setActiveSection] = useState('overview');
  const current = sections.find(s => s.id === activeSection) || sections[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] p-0 overflow-hidden" data-testid="how-to-use-dialog">
        <div className="flex h-[70vh]">
          {/* Left nav */}
          <div className="w-52 shrink-0 border-r bg-muted/30 overflow-y-auto">
            <div className="p-4 pb-2 border-b">
              <DialogHeader>
                <DialogTitle className="text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-[#C68E5D]" /> How to Use
                </DialogTitle>
              </DialogHeader>
            </div>
            <nav className="p-2">
              {sections.map(s => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-xs text-left transition-colors ${activeSection === s.id ? 'bg-[#1A2F23] text-white font-medium' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
                    data-testid={`guide-nav-${s.id}`}
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{s.title}</span>
                    {activeSection === s.id && <ChevronRight className="w-3 h-3 ml-auto shrink-0" />}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right content */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="flex items-center gap-2 mb-4">
              <current.icon className="w-5 h-5 text-[#C68E5D]" />
              <h2 className="text-base font-semibold">{current.title}</h2>
            </div>
            <div className="space-y-4">
              {current.content.map((item, i) => (
                <div key={i}>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#1A2F23] mb-1">{item.heading}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            {/* Bottom navigation */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              {sections.indexOf(current) > 0 ? (
                <button
                  onClick={() => setActiveSection(sections[sections.indexOf(current) - 1].id)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  &larr; {sections[sections.indexOf(current) - 1].title}
                </button>
              ) : <span />}
              {sections.indexOf(current) < sections.length - 1 ? (
                <button
                  onClick={() => setActiveSection(sections[sections.indexOf(current) + 1].id)}
                  className="text-xs text-[#1A2F23] font-medium hover:text-[#C68E5D] transition-colors"
                >
                  {sections[sections.indexOf(current) + 1].title} &rarr;
                </button>
              ) : <span />}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
